/**
 * « Autres pages » du compte : celles qui ne sont pas des rubriques de la
 * navigation (absences, position, astreintes, entreprise, documents, aide).
 * Une seule liste, avec ses conditions, rendue par le Menu du téléphone
 * (`MenuPage`) et par le menu du compte de l'ordinateur (`AccountMenu`) :
 * les deux ne divergent plus.
 */
import type { LucideIcon } from 'lucide-react';
import type { Capability } from '@myequip/shared';
import { useCan, type Me } from '../core/auth.js';
import { locationConcerns } from '../core/location.js';
import { t } from '../core/i18n.js';
import { ICONS } from '../design/icons.js';
import { MY_ABSENCES_PATH } from '../features/planning/absence-requests.js';
import { COMPANY_DOCUMENTS_PATH, toRead, useCompanyDocuments } from '../features/company-documents/company-documents.js';
import { LOCATION_PAGE_PATH, locationStateLabel, useLocationPermission } from '../features/profile/LocationAccess.js';
import { ONCALL_PATHS, useOncallNow, whenShort } from '../features/team/oncall-model.js';
import { useAreas } from './use-areas.js';

export interface OtherPage {
  key: 'absences' | 'position' | 'astreintes' | 'entreprise' | 'documents' | 'aide';
  to: string;
  label: string;
  icon: LucideIcon;
  /** Une ligne d'explication, montrée par le Menu du téléphone. */
  line: string;
}

export interface OtherPagesInput {
  can: (capability: Capability) => boolean;
  me: Pick<Me, 'company_name'> & Parameters<typeof locationConcerns>[0];
  /** Une rubrique Équipe existe déjà : les astreintes y sont. */
  teamArea: boolean;
  /** L'entreprise se sert des astreintes, et la prochaine ou l'actuelle de la personne. */
  oncall: { inUse: boolean; current?: { ends_at: string } | undefined; next?: { starts_at: string } | undefined } | undefined;
  positionLabel: string;
  documentsToRead: number;
  /** Écran d'où l'on demande de l'aide, pour l'assistance. */
  helpFrom?: string;
}

/** Logique pure : les pages proposées à ce compte, dans l'ordre d'affichage. */
export function otherPages(input: OtherPagesInput): OtherPage[] {
  const { can, me, oncall } = input;
  const pages: OtherPage[] = [];
  // Qui valide les absences les gère dans l'espace Équipe : pas de « Mes absences » en plus.
  if (!can('absence.approve')) {
    pages.push({
      key: 'absences', to: MY_ABSENCES_PATH, label: 'Mes absences', icon: ICONS.absences,
      line: 'Demander un congé, suivre la réponse',
    });
  }
  // Position pendant le travail (16/10/2026) : proposée de nouveau ici, jamais relancée.
  if (locationConcerns(me)) {
    pages.push({ key: 'position', to: LOCATION_PAGE_PATH, label: 'Ma position', icon: ICONS.position, line: input.positionLabel });
  }
  if (!input.teamArea && oncall?.inUse) {
    pages.push({
      key: 'astreintes', to: ONCALL_PATHS.liste, label: t('equipe.astreintes.titre'), icon: ICONS.astreinte,
      line: oncall.current
        ? `Vous êtes d’astreinte jusqu’au ${whenShort(oncall.current.ends_at)}`
        : oncall.next
          ? `Prochaine : ${whenShort(oncall.next.starts_at)}`
          : 'Qui appeler en cas d’urgence',
    });
  }
  if (can('settings.manage') || can('users.manage')) {
    pages.push({
      key: 'entreprise', to: '/entreprise', label: t('nav.monEntreprise'), icon: ICONS.entreprise,
      line: `S’applique à toute l’équipe de ${me?.company_name ?? ''}`,
    });
  }
  pages.push({
    key: 'documents', to: COMPANY_DOCUMENTS_PATH, label: 'Documents de l’entreprise', icon: ICONS.documents,
    line: input.documentsToRead > 0 ? `${input.documentsToRead} à lire` : 'Règlement intérieur, consignes',
  });
  pages.push({
    key: 'aide', to: input.helpFrom ? `/aide?depuis=${encodeURIComponent(input.helpFrom)}` : '/aide',
    label: 'Aide et contact', icon: ICONS.aide, line: 'Manuels, questions courantes, écrire à l’assistance MyEquip',
  });
  return pages;
}

/** Les « autres pages » du compte connecté, avec leurs données (astreintes, documents, position). */
export function useOtherPages(me: Me | null | undefined, helpFrom?: string, enabled = true): OtherPage[] {
  const can = useCan();
  const nav = useAreas();
  const documents = useCompanyDocuments(enabled);
  const locationPermission = useLocationPermission();
  const teamArea = nav.sections.some((section) => section.areas.some((area) => area.to === '/equipe'));
  const oncall = useOncallNow(enabled && !teamArea);
  if (!me) return [];
  return otherPages({
    can,
    me,
    teamArea,
    oncall: oncall.data
      ? { inUse: oncall.data.inUse, current: oncall.data.mine.current ?? undefined, next: oncall.data.mine.next ?? undefined }
      : undefined,
    positionLabel: locationStateLabel(locationPermission.state),
    documentsToRead: (documents.data?.items ?? []).filter(toRead).length,
    ...(helpFrom ? { helpFrom } : {}),
  });
}
